// ─────────────────────────────────────────────────────────────────────────────
// Quem pode ver o quê.
//
// O Orion nasceu como ferramenta do suporte e, por isso, todo mundo que
// entrava via tudo. Agora ele atende a empresa inteira — e a maior parte das
// telas não faz sentido fora do suporte: formalizar um ticket de helpdesk e
// registrar incidente são o trabalho de um setor específico.
//
// A separação é por SETOR. Ele vem de `space.usuarios.ope_setor` — o cadastro
// corporativo — através da view `orion.vw_operador`, que junta a identidade do
// space com o que é só do Orion (o código do MySuite e as regras de jornada).
// Ver sql/view-operador_orion-fonte-space_*.sql. O app não conhece o schema
// `space`: ele lê a view, e a view lê por ele.
//
//   SUP  → suporte. Vê tudo.
//   demais (ou em branco) → vê o que serve a qualquer pessoa da Space:
//          formalização rápida, Chamado T.I., Zarion e Configurações.
//
// EM BRANCO BLOQUEIA, de propósito. O contrário — liberar na dúvida — deixaria
// as telas do suporte abertas para a empresa toda enquanto o cadastro não
// estivesse completo, e ninguém perceberia. Faltando acesso, alguém reclama no
// mesmo dia e o cadastro é corrigido.
//
// Este arquivo é a ÚNICA fonte dessa regra. Tela nenhuma decide sozinha se
// aparece: elas perguntam aqui.
// ─────────────────────────────────────────────────────────────────────────────

import { supabase, tabelaOperador } from './supabase';
import { ORION_SCHEMA } from '../config/ambiente';

/** Valor de `ope_setor` que identifica o suporte. */
export const SETOR_SUPORTE = 'SUP';

/** Compara setor sem caixa, sem acento e sem espaço em volta. */
function normalizar(setor) {
  return String(setor || '')
    .normalize('NFD').replace(/[̀-ͯ]/g, '')
    .trim()
    .toUpperCase();
}

/** true quando o setor informado é o do suporte. */
export function ehSetorSuporte(setor) {
  return normalizar(setor) === SETOR_SUPORTE;
}

/**
 * Perfil do operador, pela view `orion.vw_operador`: `{ setor, codigo, admin }`.
 *
 * O setor vem de space.usuarios; o codigo (do MySuite) e o `ope_admin` são de
 * orion.operador. A view resolve os três — aqui nao ha nocao de schema nenhum.
 *
 * `admin` é o gerente/supervisor do suporte: quem enxerga o painel de gerência,
 * com a fila e o estado dos OUTROS operadores. Ele mora em `orion.operador`, e
 * não no space, porque é um papel DENTRO do Orion — o cadastro corporativo não
 * tem noção dele.
 *
 * Devolve `{ setor: '', codigo: null, admin: false }` quando não achar — setor
 * vazio cai no perfil restrito e admin falso fecha o painel, que é o lado
 * seguro dos dois. Uma falha de rede não pode abrir as telas do suporte para
 * quem não é, nem a fila do time para quem não a coordena.
 *
 * Lê tudo na MESMA consulta, e não pelo mapa de operadores: quem acabou de ser
 * cadastrado ainda não está no mapa, que só é montado no login.
 */
export async function carregarPerfil(email) {
  const vazio = { setor: '', codigo: null, admin: false };
  if (!email) return vazio;
  try {
    const { data, error } = await (await tabelaOperador())
      .select('ope_setor, ope_codigo, ope_admin')
      // ilike sem curinga = comparação exata sem diferenciar maiúsculas: alguns
      // e-mails estão gravados em caixa alta no cadastro.
      .ilike('ope_email', String(email).trim())
      .maybeSingle();
    if (error || !data) return vazio;
    return {
      setor: String(data.ope_setor || ''),
      codigo: Number.isFinite(data.ope_codigo) ? data.ope_codigo : null,
      // `=== true` de propósito: null (coluna nunca preenchida) tem de virar
      // false, e não um valor que passe por verdadeiro em alguma comparação
      // frouxa mais adiante.
      admin: data.ope_admin === true,
    };
  } catch (err) {
    console.warn('[Orion] Perfil do operador:', err.message);
    return vazio;
  }
}

/** Só o setor. Atalho para quem não precisa do código. */
export async function carregarSetor(email) {
  return (await carregarPerfil(email)).setor;
}

// ── Acompanhar mudanças no cadastro ──────────────────────────────────────────
//
// O setor era lido UMA vez, no login, e ficava congelado na sessão: mudar
// `ope_setor` no banco não surtia efeito nenhum até o operador sair e entrar de
// novo — e ninguém adivinha que precisa fazer isso.
//
// Agora há três gatilhos, e QUAL deles pega a mudança depende de ONDE ela foi
// feita:
//
//   1. REALTIME em orion.operador. Instantâneo, mas só para essa tabela —
//      vínculo, jornada, pausas. Mudança de SETOR feita no space NÃO passa por
//      aqui (o motivo está em `assinarOperador`);
//   2. RELÓGIO, a cada 5 minutos. Relê a view, então pega tudo: inclusive o
//      setor alterado direto em space.usuarios. É o gatilho que sustenta a
//      arquitetura nova;
//   3. FOCO. Voltar ao app relê a view. É o gesto de quem acabou de pedir "muda
//      meu acesso aí" e volta para conferir — e o atalho de quem não quer
//      esperar o relógio (há também o botão nas Configurações).
//
// Nenhum sozinho basta; juntos, o pior caso é o intervalo do relógio.

let canalOperador = null;

/**
 * Avisa que a linha DESTE operador mudou em `orion.operador`.
 *
 * O callback é um SINAL, sem dados: quem chama responde relendo o perfil pela
 * view. Não é preciosismo — o payload do realtime vem da TABELA, e a tabela
 * não é mais a fonte do setor. Aproveitar o `ope_setor` de lá sobrescreveria o
 * valor correto (o do space) pelo valor replicado, que é justamente o que esta
 * arquitetura veio desfazer.
 *
 * Consequência assumida: mudança feita direto em `space.usuarios` NÃO chega por
 * aqui. Ela é pega pelo relógio de 5 min e pela volta do foco, que releem a
 * view — ver o efeito "Cadastro do operador" em context/OrionContext.js.
 *
 * `space.usuarios` fica FORA do realtime de propósito: o postgres_changes
 * entrega a linha inteira aos inscritos, incluindo `usr_senha`, que nenhum
 * GRANT liberou. Cinco minutos de atraso valem mais que uma senha no
 * WebSocket.
 *
 * Devolve a função de cancelar. Uma assinatura por sessão — chamar de novo com
 * um canal vivo não faz nada, e é o que evita duplicar o aviso quando o efeito
 * do React roda duas vezes no modo estrito.
 *
 * O filtro é feito NO CLIENTE, comparando o e-mail: a tabela de operadores tem
 * algumas dezenas de linhas, e um filtro no servidor por e-mail esbarraria na
 * caixa alta com que alguns estão gravados.
 */
export function assinarOperador(email, aoMudar) {
  if (canalOperador || !email) return () => {};

  const meu = String(email).trim().toLowerCase();

  const mudou = (linha) => {
    if (!linha) return;
    if (String(linha.ope_email || '').trim().toLowerCase() !== meu) return;
    aoMudar();
  };

  try {
    canalOperador = supabase
      .channel('orion_operador_rt')
      .on('postgres_changes',
        { event: 'UPDATE', schema: ORION_SCHEMA, table: 'operador' },
        payload => mudou(payload.new))
      .on('postgres_changes',
        { event: 'INSERT', schema: ORION_SCHEMA, table: 'operador' },
        payload => mudou(payload.new))
      .subscribe();
  } catch (e) {
    // Realtime desligado para esta tabela não é erro: o relógio cobre.
    console.warn('[Orion] Realtime do cadastro de operador:', e.message);
    return () => {};
  }

  return () => {
    try { supabase.removeChannel(canalOperador); } catch (e) { /* já removido */ }
    canalOperador = null;
  };
}

/**
 * O que este operador pode abrir.
 *
 * Uma chave por tela, com o mesmo nome que a navegação usa em `view`. Assim a
 * Sidebar e o Shell perguntam pela mesma chave, e não há como uma esquecer o
 * que a outra bloqueou.
 */
export function permissoesDoSetor(setor, admin = false) {
  const suporte = ehSetorSuporte(setor);
  const gerente = admin === true;

  return {
    /** true = analista de suporte. É o que liga o controle de ociosidade. */
    suporte,
    /** true = gerente/supervisor (ope_admin). */
    gerente,

    // ── A tela de filas: TODO O SUPORTE ──
    //
    // Ela mostra as filas GERAIS do setor — aguardando solução, fila de
    // retorno, Setor de Sistemas, sem dono — para qualquer analista enxergar o
    // que existe e PUXAR o que está livre. Isso é trabalho de atendimento, não
    // de coordenação, e por isso o critério é o mesmo das outras telas do
    // setor: `ope_setor = SUP`.
    gerencia: suporte,

    // ── O quadro de EQUIPE: só o gerente ──
    //
    // Aqui a divisão é outra. A fila é do setor e pode ser vista por quem a
    // atende; o ESTADO DAS PESSOAS — quem está em almoço, há quanto tempo, quem
    // não apareceu hoje, quanto cada um carrega — é informação de coordenação, e
    // fica com `ope_admin`.
    //
    // Não é uma tela separada: é um bloco a mais dentro da mesma tela, que
    // simplesmente não é montado (nem consultado — ver `carregarPainel`) para
    // quem não é gerente.
    gerenciaEquipe: gerente,

    // ── Só o suporte ──
    // Formalização completa: busca o ticket no MySuite, classifica, anexa e
    // envia a interação. É o trabalho do setor.
    helpdesk: suporte,
    // Consulta de ticket por número: lê o MySuite e mostra a ficha inteira de
    // qualquer chamado. É informação de atendimento, e segue o mesmo critério
    // das outras telas de ticket.
    'busca-ticket': suporte,
    // Incidentes: o acervo de erros conhecidos, alimentado pelo atendimento.
    incidentes: suporte,
    // Histórico das formalizações — subproduto da tela de formalização; sem
    // ela, a lista fica sempre vazia.
    historico: suporte,
    // Atividade: o histórico de Ativo/pausas. Anda junto com o controle de
    // atividade, que só roda para o suporte — fora dele a tabela nunca recebe
    // uma linha, e a aba abriria vazia para sempre.
    atividade: suporte,

    // ── Todo mundo ──
    // A alternativa enxuta à formalização: entra texto, sai texto formalizado.
    // Não toca em ticket, então serve a qualquer setor.
    'formalizacao-rapida': true,
    'chamado-ti': true,
    zarion: true,
    configuracoes: true,
    // O que mudou no Orion é assunto de todo mundo que usa o Orion.
    notas: true,
    // A caixa de entrada do sininho é de cada um.
    notificacoes: true,
  };
}

/**
 * Primeira tela depois do login, conforme o perfil.
 *
 * O GERENTE ABRE NO PAINEL, na frente da Formalização. É a primeira coisa que
 * ele quer ver ao entrar — quem está de pé, o que está estourando —, e deixá-lo
 * cair na Formalização o obrigaria a um clique todas as manhãs para chegar ao
 * que veio fazer. Quem é gerente e também atende continua com a Formalização a
 * um item de distância na barra lateral.
 *
 * Esta função também é o destino de quem tenta abrir uma tela proibida (ver
 * `irParaView` em OrionContext), então a ordem aqui é, na prática, a ordem de
 * preferência de cada perfil.
 */
export function viewInicial(permissoes) {
  // Só o GERENTE abre no painel. Para o analista comum a tela de filas é uma
  // consulta — ele vai lá quando quer pegar trabalho novo —, e o dia dele
  // começa na Formalização, como sempre começou. Trocar isso mudaria a rotina
  // de treze pessoas para servir a uma.
  if (permissoes?.gerenciaEquipe) return 'gerencia';
  return permissoes?.helpdesk ? 'helpdesk' : 'formalizacao-rapida';
}
